import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { chairReportsApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * ChairReports — sysadmin-only sales + purchase reports.
 *
 * Two tabs:
 *   - Monthly: pick a cycle (YYYY-MM), see sales + purchases for that cycle
 *   - Year-End: pick a year, see day-after-Jan-pickup → Dec 31 totals
 *
 * Both views also have Preview/Download PDF buttons.
 */
export default function ChairReports() {
    const [tab, setTab] = useState('monthly')
    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Reports</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Sales and purchase totals by cycle or for the full year. Cancelled orders are excluded.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200">
                <TabButton active={tab === 'monthly'} onClick={() => setTab('monthly')}>Monthly</TabButton>
                <TabButton active={tab === 'yearend'} onClick={() => setTab('yearend')}>Year-End</TabButton>
            </div>

            {tab === 'monthly' ? <MonthlyTab /> : <YearEndTab />}
        </div>
    )
}

function TabButton({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${active
                    ? 'border-ghasc-navy text-ghasc-navy'
                    : 'border-transparent text-gray-500 hover:text-ghasc-navy'
                }`}
        >
            {children}
        </button>
    )
}

// ============================================================================
//  Monthly tab
// ============================================================================
function MonthlyTab() {
    const [cycle, setCycle] = useState(() => {
        const d = new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    })
    const [report, setReport] = useState(null)
    const [loading, setLoading] = useState(false)

    async function load() {
        setLoading(true)
        try {
            const response = await chairReportsApi.getMonthly(cycle)
            if (response.success) setReport(response.report)
            else toast.error(response.message || 'Could not load')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { load() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

    async function handlePreview() {
        try {
            const { blob } = await chairReportsApi.fetchMonthlyPdf(cycle, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch { toast.error('Could not load PDF') }
    }
    async function handleDownload() {
        try {
            const { blob, fileName } = await chairReportsApi.fetchMonthlyPdf(cycle, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url; a.download = fileName
            document.body.appendChild(a); a.click(); document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch { toast.error('Could not download') }
    }

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                    <input
                        type="month"
                        value={cycle}
                        onChange={(e) => setCycle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                </div>
                <Button onClick={load} loading={loading}>Load</Button>
                {report && (
                    <>
                        <Button variant="secondary" onClick={handlePreview}>Preview PDF</Button>
                        <Button variant="secondary" onClick={handleDownload}>Download PDF</Button>
                    </>
                )}
            </div>

            {report && <ReportBody report={report} />}
        </div>
    )
}

// ============================================================================
//  Year-end tab
// ============================================================================
function YearEndTab() {
    const [year, setYear] = useState(new Date().getFullYear())
    const [report, setReport] = useState(null)
    const [loading, setLoading] = useState(false)

    async function load() {
        setLoading(true)
        try {
            const response = await chairReportsApi.getYearEnd(year)
            if (response.success) setReport(response.report)
            else toast.error(response.message || 'Could not load')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { load() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

    async function handlePreview() {
        try {
            const { blob } = await chairReportsApi.fetchYearEndPdf(year, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch { toast.error('Could not load PDF') }
    }
    async function handleDownload() {
        try {
            const { blob, fileName } = await chairReportsApi.fetchYearEndPdf(year, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url; a.download = fileName
            document.body.appendChild(a); a.click(); document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch { toast.error('Could not download') }
    }

    const currentYear = new Date().getFullYear()
    const years = []
    for (let y = currentYear; y >= 2024; y--) years.push(y)

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <select
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <Button onClick={load} loading={loading}>Load</Button>
                {report && (
                    <>
                        <Button variant="secondary" onClick={handlePreview}>Preview PDF</Button>
                        <Button variant="secondary" onClick={handleDownload}>Download PDF</Button>
                    </>
                )}
            </div>

            {report && <ReportBody report={report} isYearEnd />}
        </div>
    )
}

// ============================================================================
//  Shared body: summary + sales + purchases tables
// ============================================================================
function ReportBody({ report, isYearEnd = false }) {

    return (
        <div className="space-y-4">
            {/* Summary */}
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-2">
                <div className="text-xs uppercase tracking-wider text-gray-500">
                    {isYearEnd ? 'Reporting Period' : 'Cycle Window'}
                </div>
                <div className="text-lg font-semibold text-ghasc-navy">
                    {isYearEnd
                        ? `${new Date(report.rangeStart).toLocaleDateString()} – ${new Date(report.rangeEnd).toLocaleDateString()}`
                        : `${new Date(report.windowStart).toLocaleDateString()} – ${new Date(report.windowEnd).toLocaleDateString()}`}
                </div>
                {!isYearEnd && (
                    <div className="text-xs text-gray-500">
                        Pickup: {new Date(report.pickupDate).toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                        })}
                    </div>
                )}

                <div className="border-t border-gray-100 pt-3 mt-3 space-y-1">
                    <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Total Sales</span>
                        <span className="text-sm font-medium">${Number(report.totalSales).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-sm text-gray-700">Total Purchases</span>
                        <span className="text-sm font-medium">${Number(report.totalPurchases).toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Sales */}
            <CategorySection title="Sales" categories={report.sales} />
            {/* Purchases */}
            <CategorySection title="Purchases" categories={report.purchases} />
        </div>
    )
}

function CategorySection({ title, categories }) {
    return (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-ghasc-navy">{title}</h3>
            </div>
            {categories.length === 0 ? (
                <div className="p-6 text-center text-gray-500 italic text-sm">
                    No items in this period.
                </div>
            ) : (
                <div className="divide-y divide-gray-100">
                    {categories.map(cat => (
                        <div key={cat.category} className="p-4">
                            <div className="flex items-baseline justify-between mb-2">
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                                    {cat.category}
                                </h4>
                                <span className="text-sm font-semibold text-ghasc-navy">
                                    ${Number(cat.subtotal).toFixed(2)}
                                </span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-xs text-gray-500 uppercase">
                                        <tr>
                                            <th className="text-left py-1">Item</th>
                                            <th className="text-right py-1 w-20">Qty</th>
                                            <th className="text-right py-1 w-24">Unit</th>
                                            <th className="text-right py-1 w-24">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cat.lines.map((line, i) => {
                                            const variant = [line.year, line.color].filter(Boolean).join(' / ')
                                            return (
                                                <tr key={i} className="border-t border-gray-100">
                                                    <td className="py-1.5">
                                                        <div className="text-gray-900">{line.description}</div>
                                                        <div className="text-xs text-gray-500 font-mono">
                                                            {line.itemNumber}
                                                            {variant && <span className="ml-2 font-sans">— {variant}</span>}
                                                        </div>
                                                    </td>
                                                    <td className="py-1.5 text-right">{line.quantity}</td>
                                                    <td className="py-1.5 text-right">${Number(line.cost).toFixed(2)}</td>
                                                    <td className="py-1.5 text-right font-medium">${Number(line.lineTotal).toFixed(2)}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}